import {gql, useQuery} from '@apollo/client';
import {useDebounce} from 'common/hook';
import Popper from 'popper.js';
import React, { useCallback } from 'react';
import {SPLIT_DELIMITER} from "../../placement/const";

type AutoCompleteProps = {
    queryType: 'city' | 'language' | 'provider' | 'county' | 'submitter';
    value: string
    setValue: (value: string) => void;
    setFocusInput: () => void;
}

type AutocompleteQueryResponse = {
    edges: Array<AutocompleteNode>;
}

type AutocompleteNode = {
    node: Autocomplete
}

type Autocomplete = {
    name: string;
}

export const PROVIDER_QUERY = gql`
    query matchedProviderQuery($name: String, $first: Int, $after: String) {
        matchedProviderQuery(name: $name, after: $after, first: $first) {
            edges {
                node {
                    name
                }
            }
        }
    }
`

export const COUNTY_QUERY = gql`
    query countyQuery($name: String, $first: Int, $after: String) {
        countyQuery(name: $name, after: $after, first: $first) {
            edges {
                node {
                    name
                }
            }
        }
    }
`

export const CITY_QUERY = gql`
    query cityQuery($name: String, $first: Int, $after: String) {
        cityQuery(name: $name, after: $after, first: $first) {
            edges {
                node {
                    name
                }
            }
        }
    }
`

export const LANGUAGE_QUERY = gql`
    query languageQuery($name: String, $first: Int, $after: String) {
        languageQuery(name: $name, after: $after, first: $first) {
            edges {
                node {
                    name
                }
            }
        }
    }
`

export const SUBMITTER_QUERY = gql`
    query submitterQuery($name: String, $first: Int, $after: String) {
        submitterQuery(name: $name, after: $after, first: $first) {
            edges {
                node {
                    name
                }
            }
        }
    }
`

function AutoCompleteQuery({value, setValue, setFocusInput, queryType}: AutoCompleteProps) {
    const lastValue = value.split(SPLIT_DELIMITER).pop()!;
    const onSelectOption = (newValue: string) => {
        const split = value.split(SPLIT_DELIMITER);
        split.pop(); // remove unfinish value
        // ignore autocomplete existed item
        if (!split.includes(newValue)) {
            split.push(newValue); // push autocomplete value
        }
        setValue(split.join(SPLIT_DELIMITER) + SPLIT_DELIMITER);
        setFocusInput();
    }

    let query = LANGUAGE_QUERY;
    if (queryType === 'city') {
        query = CITY_QUERY;
    } else if (queryType === 'provider') {
        query = PROVIDER_QUERY;
    } else if (queryType === 'county') {
        query = COUNTY_QUERY;
    } else if (queryType === 'submitter') {
        query = SUBMITTER_QUERY;
    }

    const {loading, data, error} = useQuery<{
        languageQuery: AutocompleteQueryResponse,
        cityQuery: AutocompleteQueryResponse
        matchedProviderQuery: AutocompleteQueryResponse,
        countyQuery: AutocompleteQueryResponse,
        submitterQuery: AutocompleteQueryResponse,
    }, {}>(
        query, {
            variables: {
                first: 10,
                after: null,
                name: lastValue
            },
            fetchPolicy: 'cache-first',
            skip: (lastValue.length < 2),
        });

    if (error) {
        return <div>{error.message}</div>;
    }

    if (loading) {
        return <div className='loading-text'>Loading</div>;
    }

    let queryResponse: AutocompleteQueryResponse;
    let queryTypeName: string;
    switch (queryType) {
        case 'city':
            queryResponse = data?.cityQuery!;
            queryTypeName = 'location';
            break;
        case 'language':
            queryResponse = data?.languageQuery!;
            queryTypeName = 'language';
            break;
        case 'provider':
            queryResponse = data?.matchedProviderQuery!;
            queryTypeName = 'provider';
            break;
        case 'county':
            queryResponse = data?.countyQuery!;
            queryTypeName = 'county';
            break;
        case 'submitter':
            queryResponse = data?.submitterQuery!;
            queryTypeName = 'submitter';
            break;
    }

    if (queryResponse === undefined || queryResponse.edges.length === 0) {
        return <div>{`There is no ${queryTypeName} name matched`}</div>;
    }

    return (
        <ul>
            {
                queryResponse.edges.map((edge) => {
                    return <li className='pl-2 py-1 hover:bg-gray-200' key={edge.node.name}
                               onClick={() => onSelectOption(edge.node.name)}>
                        {edge.node.name}
                    </li>;
                })
            }

        </ul>
    );
}

type Props = {
    field: string;
    fieldValue: string;
    setFieldValue: (newFieldValue: string) => void;
    borderColor?: string;
    borderHoverColor?: string;
    queryType: 'city' | 'language' | 'provider' | 'county' | 'submitter';
    inputType: 'char-only' | 'number';
    limitLength?: number
}

const numberReg = /^\d+$/;

export function AutoCompleteMultiInput({
                                           field,
                                           borderColor = 'gray-400',
                                           borderHoverColor = 'atlantis-100',
                                           fieldValue,
                                           setFieldValue,
                                           queryType,
                                           inputType,
                                           limitLength = 255
                                       }: Props) {
    const chooseValidInput = useCallback((oldValue: string, newValue: string) => {
        const lastValue = newValue.split(SPLIT_DELIMITER).slice(-1).pop()?.replace(/,/g, '');

        if (inputType === 'number') {
            if (lastValue && !numberReg.test(lastValue.trim())) {
                return oldValue;
            }
        }
        if (inputType === 'char-only') {
            if (lastValue && numberReg.test(lastValue)) {
                return oldValue;
            }
        }

        return newValue;
    }, [inputType]);
    const [dropdownPopoverShow, setDropdownPopoverShow] = React.useState(false);
    const [value, setValue] = React.useState(chooseValidInput('', fieldValue));
    const debounceValue = useDebounce(value, 1000); // delay value when input to debounce autocomplete query
    const btnDropdownRef = React.useRef(null);
    const popoverDropdownRef = React.useRef(null);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const nodeRef = React.useRef(null);

    const setValueWrapper = useCallback(
        (newValue: string) => setValue(chooseValidInput(value, newValue)),
        [value, chooseValidInput]
    );

    const setFocusInput = () => {
        if (inputRef) {
            inputRef.current?.focus();
        }
    };

    const openDropdownPopover = () => {
        // for sonar cloud (this constructor use to set position of the dropdown)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _ = new Popper(btnDropdownRef.current!, popoverDropdownRef.current!, {
            placement: "bottom"
        });
        setDropdownPopoverShow(true);
    };

    const handleClickOutside = (e: Event) => {
        if ((nodeRef.current as any).contains(e.target)) {
            return;
        }
        setFieldValue(value);
        setDropdownPopoverShow(false);
    };

    React.useEffect(() => {
        // handle click outside
        if (dropdownPopoverShow) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdownPopoverShow, value]);

    React.useEffect(() => {
        if (fieldValue === '') {
            setValueWrapper('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldValue])

    return (
        <div ref={nodeRef} className='inline-block relative text-sm w-full' data-testid={`autocomplete-${field}`}>
            <div ref={btnDropdownRef}>
                <input
                    name={field}
                    className={`block w-full bg-white border border-${borderColor} hover:border-${borderHoverColor} ` +
                    `px-4 py-2 pr-8 rounded shadow leading-tight focus:outline-none focus:shadow-outline`}
                    onClick={openDropdownPopover}
                    value={value}
                    onChange={(e) => setValueWrapper(e.target.value)}
                    autoComplete='off'
                    ref={inputRef}
                    maxLength={limitLength}
                />
            </div>
            <div ref={popoverDropdownRef}
                 className={
                     (dropdownPopoverShow ? "block " : "hidden ") +
                     "p-5 bg-white text-black text-base z-50 float-left list-none text-left rounded shadow-lg mt-1 w-full"
                 }
            >
                <AutoCompleteQuery value={debounceValue} setValue={setValueWrapper} queryType={queryType}
                                   setFocusInput={setFocusInput}/>
            </div>

        </div>
    );
}
